import React from 'react'
import PropTypes from 'prop-types'
import { Input, Label, Textarea } from 'components/Utils/Form'
import { Text } from 'components/base'
import ShareToSelectInput from './ShareToSelectInput'
import ToggleWithLabel from 'components/base/Toggle/LabelWithToggle'

export default class TargetForm extends React.Component {
  static propTypes = {
    integration: PropTypes.object.isRequired,
    shareSettings: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    values: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    children: PropTypes.node,
    formProps: PropTypes.object,
  }

  static defaultProps = {
    formProps: {},
  }

  render() {
    const targets = this.computeTargets(this.props.shareSettings.targets)
    return (
      <form
        style={{
          display: 'grid',
          gridGap: '20px 24px',
          gridTemplateColumns: '1fr 1fr',
        }}
        onSubmit={this.onSubmit}
        {...this.props.formProps}
      >
        {targets
          // don't even try to render tile and hidden targets
          .filter(target => !['tile', 'hidden'].includes(target.type))
          .map(this.renderTarget)}
        <div style={{ gridColumnEnd: 'span 2' }}>{this.props.children}</div>
      </form>
    )
  }

  computeTargets(targets) {
    return targets.reduce((targets, target) => {
      const value = this.props.values[target.id]
      if (!value || !target.options) {
        // no children to add
        return [...targets, target]
      }

      const option = target.options.find(option => option.value === value)

      if (!option || !option.children) {
        return [...targets, target]
      }

      return [...targets, target, ...this.computeTargets(option.children)]
    }, [])
  }

  renderTarget = (target, index) => {
    const gridColumnEnd = ['select'].includes(target.type) ? 'auto' : 'span 2'
    const autoFocus = index === 0
    return (
      <div key={target.id} style={{ gridColumnEnd }}>
        {this.renderFormElement(target, { autoFocus })}
      </div>
    )
  }

  renderFormElement(target, extraProps) {
    switch (target.type) {
      case 'select':
        return (
          <React.Fragment>
            <Label id={target.id}>{target.label}</Label>
            {this.renderSelect(target, extraProps)}
          </React.Fragment>
        )
      case 'text':
        return (
          <React.Fragment>
            <Label id={target.id}>{target.label}</Label>
            {this.renderText(target, extraProps)}
          </React.Fragment>
        )
      case 'textarea':
        return (
          <React.Fragment>
            <Label id={target.id}>{target.label}</Label>
            {this.renderTextarea(target, extraProps)}
          </React.Fragment>
        )
      case 'checkbox':
        return this.renderCheckbox(target, extraProps)
      default:
        return null
    }
  }

  renderSelect(target, extraProps) {
    // The other props don't force re-rendering of this component, so let's
    // ensure it gets re-rendered properly whenever options change.
    const key = JSON.stringify(target.options)
    const { required, id } = target

    return (
      <div className="position-relative flexbox">
        <ShareToSelectInput
          key={key}
          target={target}
          required={required}
          extraProps={extraProps}
          value={this.props.values[id]}
          onChange={value => {
            this.onChange(target, value)
          }}
        />
      </div>
    )
  }

  renderCheckbox(target, extraProps) {
    const currentValue = this.props.values[target.id]
    const checked = Boolean(currentValue) && currentValue !== 'false'

    return (
      <React.Fragment>
        <ToggleWithLabel
          toggleLabels={{ unchecked: 'No', checked: 'Yes' }}
          checked={checked}
          onToggle={event => {
            this.onChange(target, event.target.checked)
          }}
          className="flexbox-no-shrink"
          label={target.label}
        />
        <div>
          <Text.Detail>{target.subLabel}</Text.Detail>
        </div>
      </React.Fragment>
    )
  }

  renderText(target, extraProps) {
    const { id, required, autofocus } = target
    return (
      <Input
        required={required}
        autoFocus={autofocus}
        className="width-full"
        id={target.id}
        value={this.props.values[id] || ''}
        onChange={event => {
          this.onChange(target, event.target.value)
        }}
        {...extraProps}
      />
    )
  }

  renderTextarea(target, extraProps) {
    const { subLabel, id, required, autofocus } = target
    return (
      <div className="position-relative">
        {subLabel && (
          <Text.Detail className="margin-bottom-normal">{subLabel}</Text.Detail>
        )}
        <Textarea
          id={id}
          required={required}
          autoFocus={autofocus}
          value={this.props.values[id] || ''}
          onChange={event => {
            this.onChange(target, event.target.value)
          }}
          {...extraProps}
        />
      </div>
    )
  }

  onChange = (target, value) => {
    const resetValues = this.resetChildrenTargetValues(target)

    this.props.onChange({
      ...this.props.values,
      ...resetValues,
      [target.id]: value,
    })
  }

  // recursively reset values for children targets
  resetChildrenTargetValues = target => {
    const previousValue = this.props.values[target.id]
    if (!previousValue || !target.options) {
      return {}
    }

    const option = target.options.find(option => option.value === previousValue)

    if (!option || !option.children) {
      return {}
    }

    return option.children.reduce((childrenTargetValues, childTarget) => {
      return {
        ...childrenTargetValues,
        [childTarget.id]: null,
        ...this.resetChildrenTargetValues(childTarget),
      }
    }, {})
  }

  onSubmit = event => {
    event.preventDefault()
    event.stopPropagation()
    this.props.onSubmit()
  }
}
